import type { CalcResult, YearPolicy } from '@/domain/types'
import { DensityCurve } from './DensityCurve'
import { useApp } from '@/store/useApp'

export function PositionSection({ result, policy }: { result: CalcResult; policy: YearPolicy }) {
  const { position } = result
  const { densityMethod } = useApp()
  if (position.inTop300) {
    return (
      <section className="card bg-gradient-to-br from-brand-50 to-white">
        <h2 className="section-title">你的位置</h2>
        <div className="text-h3 text-brand-700 mb-2">前 300 名</div>
        <p className="text-ink-700">
          按邹城录取规则，前 300 分数线上的考生直接录取至邹城一中，分数达到当年前 300 线（
          <span className="tabular-nums">{policy.top300_line}</span> 分）即适用此规则。
        </p>
      </section>
    )
  }

  if (!position.band) {
    return (
      <section className="card border border-rose-100">
        <h2 className="section-title">你的位置</h2>
        <div className="text-h3 text-rose-700 mb-2">低于普通生填报志愿资格线</div>
        <p className="text-ink-700">
          当年资格线为 <span className="tabular-nums">{policy.qualification_line}</span> 分。
          本工具暂不覆盖该段考生，请咨询学校了解职普融通班等其他通道。
        </p>
      </section>
    )
  }

  const b = position.band
  const bandRange = `${b.lower} ≤ 分数 < ${b.upper}`
  return (
    <section className="card">
      <h2 className="section-title">你的位置</h2>

      <div className="grid grid-cols-2 gap-4">
        <Stat label="所在分数段" value={bandRange} />
        <Stat label="段内人数" value={`${b.people} 人`} />
        <Stat label="段内顺位" value={`第 ${position.positionInBand} 顺位`} />
        <Stat label="段内累计位置" value={`${(position.cumulativePosition * 100).toFixed(0)}%`} />
      </div>

      <p className="mt-3 text-ink-400 text-xs leading-relaxed">
        按 10 分段比例录取的规则，段内位置比总分更关键：多 1 分若恰好跨入上一段段底，
        位置反而靠后，概率可能不升反降——这是规则固有现象，并非计算错误。
      </p>

      <div className="mt-5 pt-4 border-t border-ink-100">
        <div className="text-ink-500 text-sm mb-1">估算全市名次</div>
        <div className="text-h3 text-ink-900 tabular-nums">
          约 {position.estimatedRankRange.min} – {position.estimatedRankRange.max} 名
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-ink-100">
        <div className="text-ink-500 text-sm mb-2">分数分布密度曲线（红线为你的位置）</div>
        <DensityCurve
          policy={policy}
          score={result.input.score}
          method={densityMethod}
        />
      </div>
    </section>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-ink-500 text-sm mb-1">{label}</div>
      <div className="text-h3 text-ink-900 tabular-nums">{value}</div>
    </div>
  )
}
